import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Shield, Swords, Sword, MessageSquare,
  User, LogOut, Menu, X, Globe, Crown, Settings, Library,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout, loginWithDiscord } from '@/store/slices/authSlice';
import { SupporterName } from '@/components/Common/SupporterBadge';
import { UserAvatarImg } from '@/components/Common/UserAvatarImg';

const DISCORD_SVG = (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.042.028.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
  </svg>
);

// Nav items always visible to authenticated users (in desktop sidebar + mobile menu)
const PUBLIC_ITEMS  = [
  { to: '/library', icon: Library,      label: 'Library of Nix' },
  { to: '/boards',  icon: MessageSquare, label: 'Message Board'  },
];
const AUTH_ITEMS    = [
  { to: '/worlds',     icon: Globe,         label: 'Worlds'      },
  { to: '/characters', icon: Swords,        label: 'Characters'  },
  { to: '/combat',     icon: Sword,         label: 'Combat'      },
  { to: '/profile',    icon: User,          label: 'Profile'     },
];
const DM_ITEMS      = [{ to: '/dm',    icon: Crown,    label: 'DM Dashboard' }];
const ADMIN_ITEMS   = [{ to: '/admin', icon: Settings, label: 'Admin Panel'  }];

function MobileNavLink({
  to, icon: Icon, label, active, onClick,
}: { to: string; icon: React.ElementType; label: string; active: boolean; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
        active
          ? 'bg-gold/10 text-gold font-medium'
          : 'text-parchment-muted hover:text-parchment hover:bg-darkbg-tertiary'
      }`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-gold' : 'text-parchment-muted/50'}`} />
      {label}
      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gold shrink-0" />}
    </Link>
  );
}

export default function NavBar() {
  const dispatch = useAppDispatch();
  const { user, viewAsRole, viewAsUser, supporterUserIds } = useAppSelector((s) => s.auth);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const close = () => setMobileOpen(false);
  const isCombatRoutine = location.pathname.startsWith('/characters/')
    && new URLSearchParams(location.search).get('tab') === 'routines';
  const isActive = (to: string) => {
    if (to === '/combat') return location.pathname === '/combat' || isCombatRoutine;
    if (to === '/characters' && isCombatRoutine) return false;
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  // Desktop nav — compact set shown in the top bar
  const desktopNavItems = [
    { to: '/library',    icon: Library,       label: 'Library'    },
    ...(user ? [{ to: '/characters', icon: Swords, label: 'Characters' }] : []),
    ...(user ? [{ to: '/combat', icon: Sword, label: 'Combat' }] : []),
  ];

  // ── Effective identity (two-identity model) ────────────────────────────────
  // When admin preview mode is active, viewAsUser holds the previewed user's
  // profile. All UI visibility/permission checks must use the effective role,
  // NOT user.role (which is always the real admin).
  //
  // "Am I deciding what to SHOW?" → use effectiveRole / effectiveUser
  // "Am I making an API call?"    → apiFetch handles it (always real JWT)
  //
  // Full model documented in src/store/slices/authSlice.ts.
  // This block is the canonical NavBar reference implementation.
  const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role;
  const effectiveUser = viewAsUser ?? user;
  const isDmOrAdmin = effectiveRole === 'dm' || effectiveRole === 'admin';
  // Admin Panel hidden when previewing as a non-admin — use the top banner to exit.
  const isAdmin       = effectiveRole === 'admin';
  const isSupporterMe = !!(effectiveUser?.userId && supporterUserIds.includes(effectiveUser.userId));

  return (
    <header className="sticky top-0 z-50 bg-darkbg-secondary/95 backdrop-blur-sm border-b border-darkbg-border shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
      {/* Gold accent hairline at very top */}
      <div className="h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

      <nav className="max-w-7xl mx-auto px-4 h-15 flex items-center gap-4" style={{ height: '3.75rem' }}>

        {/* ── Logo ── */}
        <Link
          to="/"
          className="font-display text-xl font-bold tracking-widest flex items-center gap-2 shrink-0
                     text-gold drop-shadow-[0_0_12px_rgba(201,168,76,0.45)]
                     hover:drop-shadow-[0_0_18px_rgba(201,168,76,0.65)] transition-all duration-200"
        >
          <Shield className="w-5 h-5" />
          DARKFELD
        </Link>

        {/* Vertical rule */}
        <div className="hidden lg:block h-5 w-px bg-darkbg-border shrink-0" />

        {/* ── Desktop nav links ── */}
        <div className="hidden lg:flex items-center gap-1 flex-1">
          {desktopNavItems.map(({ to, icon: Icon, label }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                            transition-all duration-150 ${
                              active
                                ? 'text-gold bg-gold/10'
                                : 'text-parchment-muted hover:text-parchment hover:bg-darkbg-tertiary'
                            }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {active && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-gold rounded-full" />
                )}
              </Link>
            );
          })}
        </div>

        {/* ── Auth / user area ── */}
        <div className="flex items-center gap-2 ml-auto">
          {user ? (
            <>
              {/* Avatar + name */}
              <Link to="/profile" className="flex items-center gap-2 group" onClick={close}>
                <UserAvatarImg
                  avatarUrl={effectiveUser?.avatarUrl}
                  name={effectiveUser?.username}
                  size="sm"
                  className="ring-1 ring-darkbg-border group-hover:ring-gold/50"
                  fallbackClassName="border-darkbg-border group-hover:border-gold/50"
                />
                <SupporterName
                  name={effectiveUser?.displayName ?? effectiveUser?.username ?? ''}
                  isSupporter={isSupporterMe}
                  className={`hidden lg:block text-sm ${isSupporterMe ? '' : 'text-parchment-muted group-hover:text-parchment transition-colors'}`}
                />
              </Link>

              {/* Sign out — hidden below lg (sign-out is in the mobile menu) */}
              <button
                onClick={() => dispatch(logout())}
                className="hidden lg:flex p-1.5 text-parchment-muted/50 hover:text-danger
                           rounded hover:bg-danger/10 transition-all duration-150"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => dispatch(loginWithDiscord())}
              className="hidden lg:flex btn-primary items-center gap-2 text-sm"
            >
              {DISCORD_SVG}
              Login with Discord
            </button>
          )}

          {/* ── Mobile/tablet hamburger — visible below lg where the sidebar is hidden ── */}
          <button
            className="lg:hidden p-1.5 text-parchment-muted hover:text-parchment
                       rounded hover:bg-darkbg-tertiary transition-colors"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* ── Mobile/tablet full navigation drawer — visible below lg where the sidebar is hidden ── */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-darkbg-border bg-darkbg-secondary
                        animate-slide-down max-h-[calc(100svh-3.75rem)] overflow-y-auto">

          {/* User identity strip (if logged in) */}
          {user && (
            <div className="px-4 py-3 border-b border-darkbg-border/60 bg-darkbg-tertiary/40
                            flex items-center gap-3">
              <UserAvatarImg
                avatarUrl={effectiveUser?.avatarUrl}
                name={effectiveUser?.username}
                size="md"
                className="ring-1 ring-darkbg-border"
              />
              <div className="min-w-0">
                <SupporterName
                  name={effectiveUser?.displayName ?? effectiveUser?.username ?? ''}
                  isSupporter={isSupporterMe}
                  className={`text-sm font-medium truncate block ${isSupporterMe ? '' : 'text-parchment'}`}
                />
                {effectiveUser?.displayName && effectiveUser.displayName !== effectiveUser.username && (
                  <p className="text-parchment-muted/50 text-xs truncate">Discord account linked</p>
                )}
              </div>
            </div>
          )}

          {/* Navigation items */}
          <nav className="px-3 py-3 space-y-0.5">

            {/* Public items */}
            {PUBLIC_ITEMS.map((item) => (
              <MobileNavLink key={item.to} {...item} active={isActive(item.to)} onClick={close} />
            ))}

            {/* Authenticated items */}
            {user && (
              <>
                <div className="my-2 border-t border-darkbg-border/60" />
                {AUTH_ITEMS.map((item) => (
                  <MobileNavLink key={item.to} {...item} active={isActive(item.to)} onClick={close} />
                ))}
              </>
            )}

            {/* DM / Admin items */}
            {isDmOrAdmin && (
              <>
                <div className="my-2 border-t border-darkbg-border/60" />
                {DM_ITEMS.map((item) => (
                  <MobileNavLink key={item.to} {...item} active={isActive(item.to)} onClick={close} />
                ))}
                {isAdmin && ADMIN_ITEMS.map((item) => (
                  <MobileNavLink key={item.to} {...item} active={isActive(item.to)} onClick={close} />
                ))}
              </>
            )}

            {/* Auth action */}
            <div className="mt-3 pt-3 border-t border-darkbg-border/60">
              {user ? (
                <button
                  onClick={() => { dispatch(logout()); close(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm
                             text-parchment-muted/60 hover:text-danger hover:bg-danger/8 transition-colors"
                >
                  <LogOut className="w-4 h-4 shrink-0" />
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => { dispatch(loginWithDiscord()); close(); }}
                  className="w-full btn-primary flex items-center justify-center gap-2 text-sm"
                >
                  {DISCORD_SVG}
                  Login with Discord
                </button>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
